/**
 * Per-page SEO: title (≤60 chars), description (≤155 chars), keywords.
 * Keywords targeted: Best Digital Marketing Agency, Lead generation agency Surat,
 * Digital marketing agency real estate, B2B, marble, 360 digital agency Surat, etc.
 */

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://digisutra.in'
/** Default share image — file must live at `client/public/og-image.png` → served as `/og-image.png`. */
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

export interface PageSEO {
  title: string
  description: string
  /** Optional; comma-separated for meta keywords */
  keywords?: string
  /** Override OG image for this page */
  ogImage?: string
  /** When true, set robots noindex */
  noIndex?: boolean
}

/** Path -> SEO config. Title max 60 chars, description max 155 for SERP. */
export const pageSEO: Record<string, PageSEO> = {
  '/': {
    title: 'Best Digital Marketing Agency in Surat | DigiSutra',
    description: 'DigiSutra is a 360 digital marketing agency in Surat. We help B2B, real estate & marble sellers grow with Meta Ads, SEO & lead gen. Book a strategy call.',
    keywords: 'Best Digital Marketing Agency, Lead generation agency in Surat, Digital marketing agency for real estate, B2B marketing agency, 360 Digital marketing agency in Surat, Digital marketing agency for B2B industries',
  },
  '/about': {
    title: 'About Us | Best B2B Digital Marketing Agency – DigiSutra',
    description: 'We are a growth-focused digital marketing agency for B2B, real estate & marble sellers. Strategy, systems, sustainable growth. Based in Surat, Gujarat.',
    keywords: 'B2B marketing agency, Digital marketing agency for real estate, Best social media agency in Surat, Digital marketing agency for marble seller',
  },
  '/services': {
    title: 'Digital Marketing Services | Social Media, Meta Ads, SEO – DigiSutra',
    description: 'Full-service digital marketing: Social media agency for real estate & B2B, Meta Ads, SEO, lead generation, funnel building, website development. Surat, Gujarat.',
    keywords: 'Best social media agency in Surat, Social media agency for real estate, Social media agency for Marble sellers, Lead generation agency in Surat, 360 Digital marketing agency in Surat',
  },
  '/services/social-media-marketing': {
    title: 'Social Media Marketing Agency | Brand Presence – DigiSutra',
    description: 'Best social media agency in Surat for real estate & marble sellers. We build brand presence that earns trust. Strategic content, consistent engagement.',
    keywords: 'Best social media agency in Surat, Social media agency for real estate, Social media agency for Marble sellers',
  },
  '/services/paid-ads': {
    title: 'Meta Ads & Paid Ads | Lead Generation Agency – DigiSutra',
    description: 'Lead generation agency in Surat. We run high-performance Meta Ads for B2B, real estate & marble sellers. Qualified enquiries, measurable ROI.',
    keywords: 'Lead generation agency in Surat, B2B marketing agency, Digital marketing agency for B2B industries, Meta Ads Surat',
  },
  '/services/seo': {
    title: 'SEO Services | Digital Marketing Agency – DigiSutra',
    description: 'SEO for B2B & high-value industries. Long-term visibility, organic traffic, keyword strategy. Digital marketing agency in Surat.',
    keywords: 'SEO agency Surat, Digital marketing agency for B2B industries, B2B marketing agency',
  },
  '/services/website-development': {
    title: 'Website Development | Conversion-Focused – DigiSutra',
    description: 'Websites that work like your best sales rep. Fast, mobile-first, conversion-focused. Digital marketing agency in Surat.',
    keywords: 'Website development Surat, Digital marketing agency for B2B industries',
  },
  '/services/lead-generation': {
    title: 'Lead Generation Agency in Surat | B2B & Real Estate – DigiSutra',
    description: 'Structured lead generation for B2B, real estate & marble sellers. Qualified enquiries, predictable pipelines. Best lead generation agency in Surat.',
    keywords: 'Lead generation agency in Surat, B2B marketing agency, Digital marketing agency for real estate, Digital marketing agency for marble seller',
  },
  '/services/funnel-building': {
    title: 'Funnel Building | Sales Process – DigiSutra',
    description: 'Convert prospects into customers. Awareness to decision. Funnel building for B2B and high-value businesses. Surat digital marketing agency.',
    keywords: 'Funnel building, B2B marketing agency, Lead generation agency in Surat',
  },
  '/contact': {
    title: 'Contact Us | Book a Strategy Call – DigiSutra Surat',
    description: 'Contact DigiSutra – Best digital marketing agency in Surat, Gujarat. Book a discovery call. Address: Wester Business Park, Vesu, Surat.',
    keywords: 'Contact DigiSutra, Digital marketing agency Surat, Lead generation agency in Surat',
  },
  '/insights': {
    title: 'Knowledge & Insights | Digital Marketing – DigiSutra',
    description: 'Insights on Meta Ads, lead generation, BLS method & sales systems. From the best digital marketing agency in Surat.',
    keywords: 'Digital marketing insights, B2B marketing, Lead generation, Meta Ads',
  },
  '/case-study': {
    title: 'Case Studies | Digital Marketing Results – DigiSutra',
    description: 'Case studies: How we drive growth for B2B, real estate & marble sellers. Structured marketing systems that deliver.',
    keywords: 'Digital marketing case study, B2B marketing agency, Real estate marketing',
  },
  '/privacy': {
    title: 'Privacy Policy | DigiSutra',
    description: 'Privacy policy for DigiSutra. How we collect, use and protect your data. Digital marketing agency in Surat.',
  },
  '/terms': {
    title: 'Terms & Conditions | DigiSutra',
    description: 'Terms and conditions for using DigiSutra services. Digital marketing agency in Surat, Gujarat.',
  },
  '/refund': {
    title: 'Refund Policy | DigiSutra',
    description: 'DigiSutra refund policy. All payments are non-refundable. No returns. Digital marketing agency in Surat.',
  },
  '/clients': {
    title: 'Our Clients | Businesses We Work With – DigiSutra',
    description: 'Clients who trust DigiSutra for digital marketing, lead generation and growth. B2B, real estate, marble and more. Surat, Gujarat.',
    keywords: 'DigiSutra clients, Digital marketing agency clients, B2B marketing clients Surat',
  },
  // City landing pages (GEO/local SEO)
  '/digital-marketing-agency-surat': {
    title: 'Best Digital Marketing Agency in Surat | DigiSutra',
    description: 'DigiSutra is the top digital marketing agency in Surat. We help B2B, real estate & marble sellers grow with Meta Ads, SEO & lead generation. Book a free strategy call.',
    keywords: 'digital marketing agency in Surat, best digital marketing agency Surat, SEO agency Surat, lead generation agency Surat, social media marketing Surat',
  },
  '/digital-marketing-agency-mumbai': {
    title: 'Digital Marketing Agency in Mumbai | DigiSutra',
    description: 'DigiSutra provides digital marketing services in Mumbai. B2B lead generation, Meta Ads, SEO & social media for Mumbai businesses. Get a free strategy call.',
    keywords: 'digital marketing agency Mumbai, SEO agency Mumbai, lead generation Mumbai, B2B marketing Mumbai',
  },
  '/digital-marketing-agency-navi-mumbai': {
    title: 'Digital Marketing Agency in Navi Mumbai | DigiSutra',
    description: 'DigiSutra delivers B2B digital marketing, SEO & Meta Ads in Navi Mumbai. Structured lead generation for real estate & B2B businesses.',
    keywords: 'digital marketing agency Navi Mumbai, SEO Navi Mumbai, lead generation Navi Mumbai',
  },
  '/digital-marketing-agency-bangalore': {
    title: 'Digital Marketing Agency in Bangalore | DigiSutra',
    description: 'DigiSutra provides B2B digital marketing, SEO & Meta Ads in Bangalore. Lead generation for tech, real estate & B2B firms. Get a free strategy call.',
    keywords: 'digital marketing agency Bangalore, SEO agency Bangalore, lead generation Bangalore, B2B marketing Bangalore',
  },
  '/digital-marketing-agency-hyderabad': {
    title: 'Digital Marketing Agency in Hyderabad | DigiSutra',
    description: 'DigiSutra delivers B2B digital marketing, SEO & lead generation in Hyderabad. Structured Meta Ads & social media for real estate & B2B businesses.',
    keywords: 'digital marketing agency Hyderabad, SEO Hyderabad, lead generation Hyderabad, B2B marketing Hyderabad',
  },
  '/digital-marketing-agency-silvassa': {
    title: 'Digital Marketing Agency in Silvassa | DigiSutra',
    description: 'DigiSutra provides digital marketing & B2B lead generation in Silvassa for manufacturing businesses. Meta Ads, SEO & social media. Book a call.',
    keywords: 'digital marketing agency Silvassa, B2B marketing Silvassa, manufacturing marketing Silvassa, lead generation Silvassa',
  },
  '/digital-marketing-agency-vapi': {
    title: 'Digital Marketing Agency in Vapi | DigiSutra',
    description: 'DigiSutra offers digital marketing & B2B lead generation in Vapi. SEO, Meta Ads & social media for industrial & manufacturing businesses.',
    keywords: 'digital marketing agency Vapi, SEO Vapi, lead generation Vapi, B2B marketing Vapi, industrial marketing Vapi',
  },
  '/digital-marketing-agency-valsad': {
    title: 'Digital Marketing Agency in Valsad | DigiSutra',
    description: 'DigiSutra provides digital marketing, SEO & Meta Ads for businesses in Valsad. B2B lead generation & social media. Call us.',
    keywords: 'digital marketing agency Valsad, SEO Valsad, lead generation Valsad, B2B marketing Valsad',
  },
  '/digital-marketing-agency-ahmedabad': {
    title: 'Digital Marketing Agency in Ahmedabad | DigiSutra',
    description: 'DigiSutra provides B2B digital marketing, SEO & lead generation in Ahmedabad. Meta Ads & social media for real estate, marble & B2B businesses. Book a call.',
    keywords: 'digital marketing agency Ahmedabad, best digital marketing agency Ahmedabad, SEO Ahmedabad, lead generation Ahmedabad, B2B marketing Ahmedabad',
  },
  '/digital-marketing-agency-gandhinagar': {
    title: 'Digital Marketing Agency in Gandhinagar | DigiSutra',
    description: 'DigiSutra provides digital marketing & lead generation in Gandhinagar. B2B, government-adjacent, real estate & tech businesses. SEO & Meta Ads.',
    keywords: 'digital marketing agency Gandhinagar, SEO Gandhinagar, lead generation Gandhinagar, GIFT City marketing',
  },
  '/digital-marketing-agency-vadodara': {
    title: 'Digital Marketing Agency in Vadodara (Baroda) | DigiSutra',
    description: 'DigiSutra provides B2B digital marketing, SEO & Meta Ads in Vadodara. Lead generation for manufacturing, real estate & B2B businesses.',
    keywords: 'digital marketing agency Vadodara, SEO Vadodara, lead generation Vadodara, B2B marketing Vadodara Baroda',
  },
}

/** Get SEO for path; supports dynamic /services/:slug */
export function getSEOForPath(pathname: string): PageSEO & { canonical: string } {
  const path = pathname.replace(/\/$/, '') || '/'
  const exact = pageSEO[path]
  if (exact) {
    return { ...exact, canonical: `${SITE_URL}${path === '/' ? '' : path}` }
  }
  // Dynamic: /services/:slug
  if (path.startsWith('/services/')) {
    const slug = path.replace('/services/', '')
    const serviceMeta = pageSEO[`/services/${slug}`]
    if (serviceMeta) {
      return { ...serviceMeta, canonical: `${SITE_URL}${path}` }
    }
  }
  // Dynamic: /insights/:slug
  if (path.startsWith('/insights/')) {
    return {
      title: 'Knowledge & Insights | DigiSutra',
      description: 'Insights on lead generation, Meta Ads, and digital growth. DigiSutra – best digital marketing agency in Surat.',
      canonical: `${SITE_URL}${path}`,
    }
  }
  // Dynamic: /case-study/:slug
  if (path.startsWith('/case-study/') && path !== '/case-study') {
    return {
      title: 'Case Study | DigiSutra',
      description: 'Case study: marketing results and growth systems from DigiSutra — Surat, Gujarat.',
      canonical: `${SITE_URL}${path}`,
    }
  }
  const fallback = pageSEO['/']
  return {
    ...fallback,
    canonical: `${SITE_URL}${path === '/' ? '' : path}`,
  }
}

export { SITE_URL, DEFAULT_OG_IMAGE }
